import { isAfter, parse } from 'date-fns';
import { gql } from '@apollo/client';
import { graphql } from '@xolv-mybrightlink/common';
import { CareEpisode, CareEpisodeResponse } from '../model/CareEpisode';
import { ProgressPortrait } from '../model/ProgressPortrait';
import { ProgressPortraitResponse } from '../model/ProgressPortraitResponse';
import {
  fromProgressPortraitJson,
  progressPortraitMetricsFragment,
} from './MetricsFragmentQuery';

export const fetchCareEpisodes = async (
  patientSfId: string,
): Promise<CareEpisode[]> => {
  const { data } = await graphql.query<{ careEpisodes: CareEpisodeResponse[] }>(
    {
      query: fetchCareEpisodesQuery,
      variables: {
        patientSfId: patientSfId,
      },
    },
  );
  return data.careEpisodes
    .map((episodeResponse: CareEpisodeResponse) =>
      caseEpisodeFromJson(episodeResponse),
    )
    .sort((e1: CareEpisode, e2: CareEpisode) =>
      isAfter(e1.treatmentStartDate, e2.treatmentStartDate),
    );
};

const caseEpisodeFromJson = (json: CareEpisodeResponse): CareEpisode => {
  return {
    abaCaseId: json.abaCaseId,
    treatmentStartDate: parse(
      json.treatmentStartDate,
      'yyyy-MM-dd',
      new Date(),
    ),
    treatmentEndDate: json.treatmentEndDate
      ? parse(json.treatmentEndDate, 'yyyy-MM-dd', new Date())
      : null,
  };
};

const fetchCareEpisodesQuery = gql`
  query fetchCareEpisodes($patientSfId: String) {
    careEpisodes(patientSfId: $patientSfId) {
      abaCaseId
      treatmentEndDate
      treatmentStartDate
    }
  }
`;

export const fetchProgressPortraitMetrics = async (
  patientSfId: string,
  abaCaseSfId: string,
): Promise<ProgressPortrait> => {
  const { data } = await graphql.query<{
    progressPortrait: ProgressPortraitResponse;
  }>({
    query: fetchProgressMetricsQuery,
    variables: {
      patientSfId: patientSfId,
      abaCaseSfId: abaCaseSfId,
    },
    fetchPolicy: 'no-cache',
  });
  return fromProgressPortraitJson(data.progressPortrait);
};

const fetchProgressMetricsQuery = gql`
  ${progressPortraitMetricsFragment}
  query progressPortrait($patientSfId: String, $abaCaseSfId: String) {
    progressPortrait(patientSfId: $patientSfId, abaCaseSfId: $abaCaseSfId) {
      baselineMetrics {
        ...ProgressPortraitMetricsFragment
      }
      portraitTimelineMetrics {
        ...ProgressPortraitMetricsFragment
      }
    }
  }
`;
